/**
 * SIGN IN.
 *
 * This page did not exist. The app had an onboarding flow and no way back in: a returning user, or
 * anybody whose session had expired, or anybody who cleared their browser storage, was simply locked
 * out of their own money with no route to it except registering again — which the duplicate-identity
 * guard would then, correctly, refuse.
 *
 * ## Why the errors here are so unhelpful, on purpose
 *
 * "No such account", "no PIN set" and "wrong PIN" all produce the SAME message, in the same time.
 *
 * The difference between them is an account enumeration oracle: an attacker types phone numbers and
 * watches which ones come back differently, and walks away with a list of which Kenyan mobile numbers
 * hold virtual assets. That list is worth more than any single account on this platform, and it is
 * exactly the list a social-engineering campaign starts from.
 *
 * So the message is vague, and that vagueness is a feature we are paying for in usability.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { api, LipaApiError } from '@/lib/api';
import { setToken } from '@/lib/session';
import { useSession } from '@/lib/session-context';
import { Brandmark } from '@/components/brand';
import { PinField } from '@/components/auth-fields';
import { ThemeToggle } from '@/components/theme-toggle';

export default function SignIn() {
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [phone, setPhone] = useState('+2547');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const session = await api.login(phone, pin, {
        // A stable id for this browser install, so the user can SEE this device in their settings
        // and remove it (UF_09 section 4.4). Not a hardware fingerprint: collecting one would be a
        // Data Protection Act problem we do not need, and this only has to be stable, not unique to
        // a person.
        fingerprint: deviceId(),
        name: deviceName(),
      });

      setToken(session.token);

      // Pull the real tier from the server before rendering the app. The login response carries one,
      // but the session provider is the single source of truth for it, and having two would
      // eventually mean having two answers.
      await refresh();
      navigate('/home');
    } catch (e) {
      setError(
        e instanceof LipaApiError
          ? e.detail.message
          : 'We could not sign you in. Please try again.',
      );
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-[100svh] flex-col px-4 py-8">
      <div className="flex justify-end">
        <ThemeToggle />
      </div>

      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
        <header className="mb-8 flex flex-col items-center text-center">
          <Brandmark size="lg" />
          <p className="mt-4 text-sm text-ink-muted">Welcome back. Sign in to your wallet.</p>
        </header>

        <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
          {error ? (
            <div
              role="alert"
              className="mb-4 rounded-field border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
            >
              {error}
            </div>
          ) : null}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-ink">
                Phone number
              </label>
              <input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                required
                className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 font-mono text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-ink">PIN</label>
                <Link to="/reset-pin" className="text-xs font-medium text-brand hover:underline">
                  Forgot PIN?
                </Link>
              </div>
              <PinField value={pin} onChange={setPin} length={4} autoFocus invalid={!!error} />
            </div>

            <button
              type="submit"
              disabled={busy || pin.length < 4}
              className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Signing you in...' : 'Sign in'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-ink-subtle">
            Five wrong attempts locks the account for fifteen minutes. That is what makes a short
            PIN safe to use at all.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-ink-muted">
          New here?{' '}
          <Link to="/" className="font-medium text-brand underline underline-offset-2">
            Create an account
          </Link>
        </p>
      </div>
    </main>
  );
}

/**
 * A stable, opaque id for this browser install.
 *
 * Not a hardware identifier and not a fingerprint of the user. It exists for exactly one purpose: so
 * the participant can look at their list of signed-in devices and recognise which one is which, and
 * revoke the one that is not theirs (UF_09, "this was not me").
 */
function deviceId(): string {
  const KEY = 'lipa.device.id';

  try {
    const existing = window.localStorage.getItem(KEY);
    if (existing) return existing;

    const fresh = crypto.randomUUID();
    window.localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID(); // storage unavailable: a per-session device, which is honest
  }
}

function deviceName(): string {
  const ua = navigator.userAgent;

  if (/iPhone|iPad/.test(ua)) return 'iPhone or iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'Web browser';
}
